import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { callAdminAPI } from '../../api/callAdmin.js';

function todayISO() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}

const STATUS_STYLE = {
  called:  { bg: 'rgba(22,163,74,0.1)',   color: '#16a34a' },
  ordered: { bg: 'rgba(22,163,74,0.1)',   color: '#16a34a' },
  pending: { bg: 'rgba(234,179,8,0.12)',  color: '#a16207' },
  skipped: { bg: 'rgba(107,114,128,0.1)', color: '#6b7280' },
};

const CALL_TYPE_LABEL = {
  scheduled:     'Scheduled',
  follow_up:     'Follow-up',
  org_follow_up: 'Org F/U',
  manual:        'Manual',
  carryover:     'Carryover',
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '5px',
      fontSize: '0.72rem', fontWeight: 700, background: s.bg, color: s.color }}>
      {status}
    </span>
  );
}

export default function DailyCallTrackerDetailPage() {
  const { date, userId } = useParams();
  const location         = useLocation();
  const navigate         = useNavigate();

  const userName = location.state?.userName ?? `User #${userId}`;
  const isToday  = date === todayISO();

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive]   = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await callAdminAPI.getDailyTracker({ date, user_id: userId });
      setRows(res.data?.data || []);
      setIsLive(res.data?.live ?? false);
    } catch {
      setRows([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [date, userId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60 s for today
  useEffect(() => {
    if (!isToday) return;
    const id = setInterval(() => load(true), 60_000);
    return () => clearInterval(id);
  }, [isToday, load]);

  const called  = rows.filter(r => r.status === 'called' || r.status === 'ordered').length;
  const pending = rows.filter(r => r.status === 'pending').length;
  const skipped = rows.filter(r => r.status === 'skipped').length;

  return (
    <div className="page-wrapper" style={{ background: '#fff', minHeight: '100vh' }}>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>

      {/* Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {/* Back button */}
          <button
            onClick={() => navigate('/daily-call-tracker')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px',
              borderRadius: '8px', border: '1.5px solid #e5e7eb', background: '#f9fafb',
              fontSize: '0.82rem', fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
            ← Back
          </button>

          {/* Avatar + name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '50%',
              background: 'rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontWeight: 800, fontSize: '1rem', color: '#6366f1', flexShrink: 0 }}>
              {userName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h1 className="page-title" style={{ margin: 0 }}>{userName}</h1>
                {isLive && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '2px 8px', borderRadius: '20px', fontSize: '0.68rem', fontWeight: 700,
                    background: 'rgba(22,163,74,0.1)', color: '#16a34a' }}>
                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#16a34a',
                      animation: 'pulse 1.5s ease-in-out infinite' }} />
                    LIVE
                  </span>
                )}
              </div>
              <p className="page-desc" style={{ margin: 0 }}>{date}</p>
            </div>
          </div>
        </div>

        {/* Date picker */}
        <input
          type="date"
          value={date}
          onChange={e => navigate(`/daily-call-tracker/${e.target.value}/${userId}`, { state: { userName }, replace: true })}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb',
            fontSize: '0.88rem', fontWeight: 600, color: '#374151', outline: 'none', cursor: 'pointer' }}
        />
      </div>

      {/* Stat pills */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        {!loading && (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700,
              background: 'rgba(99,102,241,0.08)', color: '#6366f1' }}>
              {rows.length} assigned
            </span>
            <span style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700,
              background: 'rgba(22,163,74,0.1)', color: '#16a34a' }}>
              {called} called
            </span>
            <span style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700,
              background: 'rgba(234,179,8,0.12)', color: '#a16207' }}>
              {pending} pending
            </span>
            {skipped > 0 && (
              <span style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700,
                background: 'rgba(107,114,128,0.08)', color: '#6b7280' }}>
                {skipped} skipped
              </span>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af', fontSize: '0.9rem' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <h3>No entries for this user on {date}</h3>
        </div>
      ) : (
        <div className="table-card">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'center' }}>Calls</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id}>
                    <td className="text-muted">{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>
                      {r.lead_name || r.org_name || `ID ${r.lead_id || r.org_id}`}
                      {r.is_carryover && (
                        <span style={{ marginLeft: '7px', fontSize: '0.68rem', fontWeight: 700,
                          padding: '1px 5px', borderRadius: '3px',
                          background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>
                          C/O
                        </span>
                      )}
                    </td>
                    <td style={{ color: '#6b7280', fontSize: '0.83rem' }}>
                      {CALL_TYPE_LABEL[r.call_type] || r.call_type}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 700 }}>{r.calls_made}</td>
                    <td><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
